import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";

export default function Login() {
  useSEO({
    title: "Sign in",
    description: "Sign in to SonSpeak — the AAC communication app for nonverbal kids and adults.",
    path: "/login",
  });
  const { user, checked, login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const inviteCode = params.get("invite");
  const refCode = params.get("ref");
  const successPath = inviteCode
    ? `/invite/${inviteCode}`
    : refCode
    ? `/refer/${refCode}`
    : "/pick-board";
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  // A failed Google sign-in bounces back here with the reason in navigation
  // state — surface it instead of a silent return to the form.
  const [error, setError] = useState(location.state?.error || "");
  const [busy, setBusy] = useState(false);

  // Don't render the form while AuthContext is still deciding whether the
  // user is already signed in — otherwise the login form flashes for a
  // frame before Navigate fires and users perceive it as "existing session
  // wasn't detected."
  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--cream)]" data-testid="login-checking">
        <div className="display text-2xl text-[color:var(--ink-soft)]">Checking your session…</div>
      </div>
    );
  }
  if (user) return <Navigate to={successPath} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) nav(successPath);
    else setError(res.error);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signInWithGoogle = () => {
    const redirectUrl = window.location.origin + successPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[color:var(--cream)]">
      <div className="brutal-card p-8 w-full max-w-md bg-[color:var(--paper)]">
        <div className="text-center mb-6">
          <img
            src="/brand/sonspeak-logo-transparent.png"
            alt="SonSpeak"
            className="h-16 w-auto mx-auto mb-3"
            data-testid="login-logo"
          />
          <h1 className="display text-3xl font-extrabold">Welcome back</h1>
          <p className="text-[color:var(--ink-soft)] mt-1">Sign in to your caregiver account</p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="display font-semibold mb-1 block">Email</span>
            <input
              className="brutal-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="login-email"
            />
          </label>
          <label className="block">
            <span className="display font-semibold mb-1 block">Password</span>
            <input
              className="brutal-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="login-password"
            />
          </label>

          {error && (
            <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3" data-testid="login-error">
              <div className="font-bold text-base">{error}</div>
              {/invalid email or password/i.test(error) && (
                <div className="text-sm mt-2">
                  Forgot your password?{" "}
                  <Link to={`/forgot-password?email=${encodeURIComponent(email)}`} className="font-bold underline">
                    Reset it →
                  </Link>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            className="pill-btn primary w-full"
            disabled={busy}
            data-testid="login-submit"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="flex items-center gap-2 my-1 text-xs uppercase tracking-[0.25em] text-[color:var(--ink-soft)]">
            <span className="flex-1 h-[2px] bg-[color:var(--ink)] opacity-20" />
            or
            <span className="flex-1 h-[2px] bg-[color:var(--ink)] opacity-20" />
          </div>

          <button
            type="button"
            className="pill-btn w-full"
            onClick={signInWithGoogle}
            data-testid="login-google"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.97 10.97 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            <span>Sign in with Google</span>
          </button>
        </form>

        <div className="text-center mt-5 text-sm text-[color:var(--ink-soft)]">
          New here?{" "}
          <Link to="/register" className="text-[color:var(--coral-deep)] font-bold underline" data-testid="login-to-register">
            Create an account
          </Link>
        </div>

        <div className="text-center mt-4">
          <a
            href="https://emergent.sh"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink)] transition-colors"
            data-testid="login-emergent-badge"
          >
            <span aria-hidden className="inline-block w-2 h-2 rounded-full bg-[color:var(--gold)]" />
            Built with Emergent
          </a>
        </div>
      </div>
    </div>
  );
}
